import { z } from 'zod';

import {
  attachmentStatusSchema,
  authorTypeSchema,
  conversationStatusSchema,
  directionSchema,
  sendStatusSchema,
} from '../enums';
import { toneSchema } from '../connector/common';
import { translationLangSchema } from '../constants';

/**
 * События realtime (сервер → клиент).
 *
 * Два правила, оба вытекают из разбора предшественника:
 *
 *   1. В событии едут ИДЕНТИФИКАТОРЫ и мелкие скаляры, а не объекты целиком.
 *      Полное сообщение с вложениями и переводами клиент забирает по REST —
 *      иначе любое изменение формы сущности ломает совместимость сокета,
 *      а событие раздувается до килобайтов.
 *   2. У каждого события есть projectId. Он определяет комнату (см. rooms.ts),
 *      а комнаты — единственное, что не даёт повторить broadcast всем подряд:
 *      оператор без membership на проект не должен получить даже факт события.
 *
 * Скаляры вроде direction или sendStatus едут вместе с id осознанно: без них
 * клиенту пришлось бы делать запрос на КАЖДОЕ входящее только чтобы понять,
 * увеличивать ли счётчик непрочитанных.
 */

/**
 * Все события идут одним socket.io-каналом, а не отдельным именем на тип.
 * Клиент вешает один обработчик и разбирает discriminated union; добавление
 * нового типа события не требует правок в подписке.
 */
export const WS_EVENT_NAME = 'support:event';

const base = {
  projectId: z.uuid(),
  /** Серверное время события — по нему клиент отбрасывает опоздавшие апдейты. */
  at: z.iso.datetime({ offset: true }),
};

export const wsEventSchema = z.discriminatedUnion('type', [
  z.object({
    ...base,
    type: z.literal('message.created'),
    conversationId: z.uuid(),
    messageId: z.uuid(),
    /** IN — поднять непрочитанные и дать звук; OUT — просто дорисовать. */
    direction: directionSchema,
    authorType: authorTypeSchema,
  }),
  z.object({
    ...base,
    type: z.literal('message.updated'),
    conversationId: z.uuid(),
    messageId: z.uuid(),
    /**
     * Присутствует, когда обновление про доставку: QUEUED → SENT либо FAILED.
     * Оптимистично отрисованное сообщение меняет галочку без похода в REST.
     */
    sendStatus: sendStatusSchema.optional(),
  }),
  z.object({
    ...base,
    type: z.literal('message.deleted'),
    conversationId: z.uuid(),
    messageId: z.uuid(),
  }),
  z.object({
    ...base,
    type: z.literal('conversation.created'),
    conversationId: z.uuid(),
    contactId: z.uuid(),
    botId: z.uuid(),
  }),
  z.object({
    ...base,
    type: z.literal('conversation.updated'),
    conversationId: z.uuid(),
    /** Есть, если сменился статус: диалог переезжает между папками. */
    status: conversationStatusSchema.optional(),
    unreadCount: z.number().int().min(0).optional(),
  }),
  z.object({
    ...base,
    type: z.literal('conversation.assigned'),
    conversationId: z.uuid(),
    /** null — назначение снято. */
    assigneeId: z.uuid().nullable(),
    /** Кто назначил: сам взял или назначил супервизор. */
    byUserId: z.uuid().nullable(),
  }),
  z.object({
    ...base,
    type: z.literal('conversation.read'),
    conversationId: z.uuid(),
    /** Оператор, прочитавший диалог: его вкладки должны сбросить счётчик. */
    byUserId: z.uuid(),
    unreadCount: z.number().int().min(0),
  }),
  z.object({
    ...base,
    type: z.literal('presence.typing'),
    conversationId: z.uuid(),
    /** Печатает ОПЕРАТОР: Telegram о наборе текста клиентом не сообщает. */
    userId: z.uuid(),
    typing: z.boolean(),
  }),
  z.object({
    ...base,
    type: z.literal('presence.online'),
    userId: z.uuid(),
    online: z.boolean(),
  }),
  z.object({
    ...base,
    type: z.literal('translation.ready'),
    conversationId: z.uuid(),
    messageId: z.uuid(),
    targetLang: translationLangSchema,
  }),
  z.object({
    ...base,
    type: z.literal('attachment.ready'),
    conversationId: z.uuid(),
    messageId: z.uuid(),
    attachmentId: z.uuid(),
    /** PENDING сюда не приходит: событие про завершение обработки медиа. */
    status: attachmentStatusSchema.exclude(['PENDING']),
  }),
  z.object({
    ...base,
    type: z.literal('project.event'),
    eventId: z.uuid(),
    /** null, если событие пришло на контакт без открытого диалога. */
    conversationId: z.uuid().nullable(),
    contactId: z.uuid().nullable(),
    /** ProjectEvent.type; поле type занято дискриминатором. */
    eventType: z.string().min(1),
    tone: toneSchema,
  }),
]);

export type WsEvent = z.infer<typeof wsEventSchema>;
export type WsEventType = WsEvent['type'];

/** Сужение по типу: WsEventOf<'message.created'> — ветка с direction. */
export type WsEventOf<T extends WsEventType> = Extract<WsEvent, { type: T }>;

export const WS_EVENT_TYPES = wsEventSchema.options.map((option) => option.shape.type.value);

// ────────────────────── Клиент → сервер ──────────────────────

/**
 * Единственное, что клиент шлёт сам. Подписку на комнаты клиент НЕ выбирает:
 * их назначает сервер по membership при подключении — иначе оператор попросту
 * подпишется на чужой проект.
 */
export const wsClientTypingSchema = z.object({
  conversationId: z.uuid(),
  typing: z.boolean(),
});
export type WsClientTyping = z.infer<typeof wsClientTypingSchema>;

export const WS_CLIENT_TYPING_NAME = 'support:typing';
